import { useState } from 'react';

import { TertiaryButton } from '@changegps/horizon-component-library';
import { Skeleton, Stack, Typography, useTheme } from '@mui/material';
import { useBoolean } from 'usehooks-ts';

import { useUserInfo } from '@/features/users';

import { BILLING_DATA } from '../constants';
import { useBillingInfo } from '../hooks';
import { formatDate } from '../utils';

import { Section } from './layout/Section';
import { BillingDrawer } from './drawer';

export const Billing = () => {
  const theme = useTheme();

  const { company_id, user_type } = useUserInfo();
  const { data, isFetching } = useBillingInfo(company_id, user_type);

  const { value: isCollapsed, toggle: toggleIsCollapsed } = useBoolean(true);
  const { value: isDrawerVisible, toggle: toggleIsDrawerVisible } =
    useBoolean();

  const [billingDrawerType, setBillingDrawerType] = useState<
    'card' | 'billing'
  >('card');

  const getBillingDetail = (id: string) => {
    switch (id) {
      case 'next-billing-date':
        return formatDate(data?.next_billing_date);
      case 'annual-cycle-renews-on':
        return formatDate(data?.renewal_date);
      case 'total-licenses':
        return data?.total_licences;
      case 'total-licenses-in-use':
        return data?.total_used_licences;
      default:
        return 'Not Available';
    }
  };

  const handleBillingDrawerToggle = (title: 'card' | 'billing') => {
    setBillingDrawerType(title);
    toggleIsDrawerVisible();
  };

  return (
    <Section
      title="Billing"
      isCollapsed={isCollapsed}
      toggleIsCollapsed={toggleIsCollapsed}
    >
      <Stack pt={2} direction="row" spacing={4} flexWrap="wrap" useFlexGap>
        {BILLING_DATA.map((item, index) => {
          if (
            user_type !== 'super-admin' &&
            item.id === 'change-billing-details'
          )
            return null;
          return (
            <Stack
              key={index}
              width={180}
              borderTop={2}
              borderColor={theme.palette.primary.main}
              pt={1}
              spacing={1.5}
            >
              <Typography>{item.label}</Typography>
              {item.hasValue ? (
                isFetching ? (
                  <Skeleton variant="text" height={32} width="50%" />
                ) : (
                  <Typography fontSize={18} fontWeight="700">
                    {getBillingDetail(item.id)}
                  </Typography>
                )
              ) : (
                <TertiaryButton
                  onClick={() =>
                    handleBillingDrawerToggle(
                      item.id === 'change-card-details' ? 'card' : 'billing',
                    )
                  }
                >
                  Open
                </TertiaryButton>
              )}
            </Stack>
          );
        })}
      </Stack>
      <BillingDrawer
        isVisible={isDrawerVisible}
        toggleIsVisible={toggleIsDrawerVisible}
        type={billingDrawerType}
        billingDetails={data?.billing_details}
      />
    </Section>
  );
};
